import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { Scale, Lock, Mail, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://static.prod-images.emergentagent.com/jobs/61fd4980-ab9a-4d57-b8f1-cc407a178545/images/1c7083102a4a2d971881b410c052aebb496b3af81f6334bf180964889e46d8af.png";

export default function Login() {
  const { login, setUser, refresh } = useAuth();
  const { t, lang, setLang } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Admin@2026!");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null); // { challenge_token, email }
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      // call api directly so we can detect 2FA challenge response
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setTwofa(data);
      } else {
        setUser(data);
        toast.success(lang === "el" ? "Επιτυχής σύνδεση" : "Signed in");
        navigate("/");
      }
    } catch (er) {
      const msg = er?.response?.data?.detail || (lang === "el" ? "Αποτυχία σύνδεσης" : "Login failed");
      setErr(typeof msg === "string" ? msg : "Σφάλμα");
    } finally { setLoading(false); }
  };

  const verifyTwofa = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login/2fa", { challenge_token: twofa.challenge_token, code });
      setUser(data);
      toast.success("2FA επιβεβαιώθηκε");
      navigate("/");
    } catch (er) {
      const msg = er?.response?.data?.detail || "Σφάλμα 2FA";
      setErr(typeof msg === "string" ? msg : "Σφάλμα");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Hero side */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-border/40">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60"/>
        <div className="absolute inset-0 bg-gradient-to-br from-background/85 via-background/40 to-background/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="size-11 rounded-lg flex items-center justify-center bg-[hsl(var(--gold)/0.15)] border gold-border">
              <Scale className="size-6 gold-text" strokeWidth={1.6}/>
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ΙΧΝΟΔΙΚΗΣ</div>
              <div className="text-[11px] text-muted-foreground mt-1.5">κάθε συμπέρασμα, δεμένο στο ίχνος του</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-[1.05] mb-5">
              Επιχειρησιακό κέντρο <span className="gold-text italic">ποινικής υπεράσπισης.</span>
            </h1>
            <p className="text-muted-foreground text-[15px] leading-relaxed">
              Οργανώστε τη δικογραφία, χτίστε την υπερασπιστική γραμμή και αξιοποιήστε
              AI επιπέδου elite litigation associate — όλα σε ένα premium workspace.
            </p>
          </div>
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
            Confidentiality · Precision · Authority
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Scale className="size-5 gold-text"/>
            <span className="font-heading font-bold">ΙΧΝΟΔΙΚΗΣ</span>
          </div>
          <div className="mb-8">
            <div className="font-mono text-[11px] gold-text uppercase tracking-[0.25em] mb-3">{t("login")}</div>
            <h2 className="font-display text-4xl font-semibold leading-tight mb-2">{t("welcome_back")}</h2>
            <p className="text-muted-foreground text-[13.5px]">{t("sign_in_subtitle")}</p>
          </div>
          <form onSubmit={twofa ? verifyTwofa : submit} className="space-y-4" data-testid="login-form">
            {!twofa ? (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("email")}</label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"/>
                    <input
                      data-testid="login-email-input"
                      type="email" required autoFocus
                      value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full bg-muted/30 border border-border/80 rounded-md pl-9 pr-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("password")}</label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"/>
                    <input
                      data-testid="login-password-input"
                      type="password" required
                      value={password} onChange={e => setPassword(e.target.value)}
                      className="w-full bg-muted/30 border border-border/80 rounded-md pl-9 pr-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div data-testid="login-2fa-step">
                <div className="flex items-center gap-2 mb-2 gold-text">
                  <Shield className="size-4"/>
                  <span className="font-heading font-semibold text-[13.5px]">Two-Factor Authentication</span>
                </div>
                <p className="text-[12.5px] text-muted-foreground mb-3">Εισάγετε τον 6-ψήφιο κωδικό από την authenticator app σας.</p>
                <input
                  data-testid="login-2fa-input"
                  type="text" inputMode="numeric" maxLength={6} required autoFocus
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g,''))}
                  placeholder="123456"
                  className="w-full bg-muted/30 border border-border/80 rounded-md px-3 py-3 text-[20px] tracking-[8px] font-mono text-center outline-none focus:border-[hsl(var(--gold)/0.5)]"
                />
              </div>
            )}
            {err && <div className="text-red-400 text-[12px] font-mono" data-testid="login-error">{err}</div>}
            <button
              data-testid="login-submit"
              type="submit" disabled={loading || (twofa && code.length < 6)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13.5px] hover:opacity-90 disabled:opacity-60 transition-all"
            >
              {loading ? t("loading") : (twofa ? "Επιβεβαίωση 2FA" : t("login"))} <ArrowRight className="size-4"/>
            </button>
            {twofa && (
              <button type="button" onClick={() => { setTwofa(null); setCode(""); setErr(""); }}
                className="w-full text-[12px] text-muted-foreground hover:text-foreground" data-testid="login-2fa-cancel">
                ← Επιστροφή
              </button>
            )}
          </form>
          <div className="flex items-center justify-between mt-6 text-[12.5px]">
            <span className="text-muted-foreground">{t("no_account")}{" "}
              <Link to="/register" className="gold-text hover:underline" data-testid="login-to-register">{t("create_account")}</Link>
            </span>
            <Link to="/forgot-password" className="text-muted-foreground hover:gold-text" data-testid="login-to-forgot">
              {t("forgot_password")}
            </Link>
          </div>
          <div className="text-center mt-3">
            <button onClick={() => setLang(lang === "el" ? "en" : "el")} className="text-muted-foreground hover:text-foreground font-mono uppercase tracking-wider text-[11px]" data-testid="login-lang-toggle">
              {lang === "el" ? "EN" : "EL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
